/**
 * @file 现货交易页相关通用方法
 * @date 2021-08-09
 */

import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { toFormat, toFixed, minus } from '@/utils/number';
import BN from 'bignumber.js';
import { error as errorToast } from '@/utils/toast';
import { useLegal } from '@/hooks/legal';
import { MARGIN_MODE_MEUM } from '@/config/user';

export function useSymbol() {
    const store = useStore();
    const symbol = computed(() => store.getters['settings/symbolMap'][store.state.exchange.symbolId] || {});
    return symbol;
}

export function useBaseBalance() {
    const store = useStore();
    const symbol = useSymbol();
    const balance = computed(() => store.getters['balance/balanceMap'][symbol.value.baseTokenId]);
    return balance;
}

export function useQuoteBalance() {
    const store = useStore();
    const symbol = useSymbol();
    const balance = computed(() => store.getters['balance/balanceMap'][symbol.value.quoteTokenId]);
    return balance;
}

export function useKlineinfo() {
    const store = useStore();
    const klineinfo = computed(() => store.state.exchange.klineinfo);
    return klineinfo;
}

export function useLtInfo() {
    const store = useStore();
    const ltInfo = computed(() => store.state.exchange.ltRisk);
    return ltInfo;
}

/**
 * 创新区方法
 *
 * @return { boolean } isInnovate   是否为创新区币种
 * @return { string }  remainQuota  剩余额度
 * @return { string }  totalQuota   总额度
 */
export function useInnovate() {
    const store = useStore();
    const { usdtToQuote } = useLegal();
    const isUTA = computed(() => store.getters['user/isUTA']);
    const symbol = useSymbol();
    const isInnovate = computed(() => Boolean(symbol?.value?.mark?.includes('innovation')));

    const quotaMap = computed(() => {
        let totalQuota = null;
        let remainQuota = null;
        if (!isUTA.value) {
            const innovateQuota = isInnovate.value && store.getters['balance/quotaMap'][symbol.value.symbolId];
            totalQuota = innovateQuota?.totalAmount || '--';
            remainQuota = innovateQuota?.remainAmount || '--';
        } else {
            const quoteToken = symbol?.value?.quoteTokenId;
            const innovateQuota = isInnovate.value && store.getters['balance/quotaMap'][symbol.value.baseTokenId];
            const balance = store.getters['balance/balanceMap'][symbol.value.baseTokenId];
            const totalAmount = innovateQuota?.limit || '--';
            const useAmount = balance?.occAssetUSDTValue || '--';
            if (totalAmount === '--') { // 总额无数据
                totalQuota = '--';
                remainQuota = '--';
            } else if (Number(totalAmount) === -1) { // 无限额
                totalQuota = '-';
                remainQuota = '-';
            } else if (useAmount === '--') { // 总额有数据 占用额无数据
                totalQuota = usdtToQuote(quoteToken, totalAmount);
                remainQuota = '--';
            } else if (minus(totalAmount, useAmount) > 0) { // 正常情况
                totalQuota = usdtToQuote(quoteToken, totalAmount);
                remainQuota = usdtToQuote(quoteToken, minus(totalAmount, useAmount));
            } else { // 小于等于0时可用额度为0
                totalQuota = usdtToQuote(quoteToken, totalAmount);
                remainQuota = '0';
            }
        }

        return {
            totalQuota,
            remainQuota,
        };
    });

    return {
        totalQuota: computed(() => quotaMap.value.totalQuota),
        remainQuota: computed(() => quotaMap.value.remainQuota),
        isInnovate,
    };
}

export function usePrecisions() {
    const symbol = useSymbol();

    const precisions = computed(() => ({
        price: symbol.value.pricePrecision || 0,
        quantity: symbol.value.quantityPrecision || 0,
        amount: symbol.value.amountPrecision || 0,
    }));

    return precisions;
}

export function useFormat() {
    const store = useStore();
    const map = { price: 'pricePrecision', quantity: 'quantityPrecision', amount: 'amountPrecision' };
    const getPrecision = (symbolId, name) => {
        const key = map[name];
        const id = symbolId || store.state.exchange.symbolId;
        const symbolMap = store.getters['settings/symbolMap'];
        if (symbolMap[id]) return symbolMap[id][key] || 0; // 如果交易对存在时，默认精度是0，可能存在配置精度为0的情况
        // 交易对不存在时，价格 默认精度是13
        if (name === 'price') {
            return 13;
        }
        // 交易对不存在时，数量/额度 默认精度是8
        return 8;
    };

    const toFormatPrice = (value, symbolId) => toFormat(value, getPrecision(symbolId, 'price'));
    const toFormatQuantity = (value, symbolId) => toFormat(value, getPrecision(symbolId, 'quantity'));
    const toFormatAmount = (value, symbolId) => toFormat(value, getPrecision(symbolId, 'amount'));
    const toFixedPrice = (value, symbolId) => toFixed(value, getPrecision(symbolId, 'price'));
    const toFixedQuantity = (value, symbolId) => toFixed(value, getPrecision(symbolId, 'quantity'));
    const toFixedAmount = (value, symbolId) => toFixed(value, getPrecision(symbolId, 'amount'));

    const toFormatQuantityPrec = (value, symbolId) => {
        let cache = '--';

        if (value !== '--') {
            cache = toFormat(value, getPrecision(symbolId, 'quantity'), 1);
            const int = cache.replace(/,/g, '').split('.')[0];
            const strLen = () => {
                const baseLen = cache.split(',').length - 1;
                if (int.length < 9) {
                    return 10 + baseLen;
                }
                if (int.length === 9) {
                    return 11;
                }
                if (int.length < 19) {
                    return 9;
                }
                return 3 + baseLen;
            };
            if (int - 0 < 10000 * 10000 * 10) {
                cache = cache.substring(0, strLen());
            } else {
                const cacheFormat = (unit, isSpell = true) => {
                    const baseStr = cache.substring(0, strLen());
                    const frontPart = baseStr.substring(0, baseStr.lastIndexOf(','));
                    const endPart = baseStr.substring(baseStr.lastIndexOf(',') + 1);
                    if (isSpell) {
                        return `${frontPart}.${endPart}${unit}`;
                    }
                    return `${baseStr}${unit}`;
                };

                // >=10亿 && < 万亿
                if (int.length >= 10 && int.length < 13) {
                    cache = cacheFormat('M');

                    // >=万亿 && < 千万亿
                } else if (int.length >= 13 && int.length < 16) {
                    cache = cacheFormat('B');

                    // >=千万亿
                } else if (int.length >= 16 && int.length < 19) {
                    cache = cacheFormat('T');
                } else {
                    cache = cacheFormat('T', false);
                }
            }
        }
        return cache;
    };

    return {
        toFormatPrice,
        toFormatQuantity,
        toFormatQuantityPrec,
        toFormatAmount,
        toFixedPrice,
        toFixedQuantity,
        toFixedAmount,
    };
}

/**
 * 获取最大可买/可卖数量 需求文档：https://confluence.yijin.io/pages/viewpage.action?pageId=83601950 四.3
 *
 *  分支逻辑:
 *  --- 未开启杠杆交易功能：使用计价货币或基础货币余额
 *  --- 已开启杠杆交易功能：
 *          --- 当前交易对 不支持 杠杆交易：使用后端计算后的 最大可买/可卖值
 *              --- 特殊情况：在买方向交易时，如果当前交易对为创新区货币，取 min(剩余限额，最大可买)
 *          --- 当前交易对 支持 杠杆交易：
 *              --- 用户使用现货模式：返回计价货币可用余额 或 基础货币可用余额
 *              --- 用户使用杠杆交易：返回后段计算的最大可买，最大可卖。
 *
 * @param { any } direction 自定义方向(lt申购赎回)
 * @return { any } 最大可买/可卖数量，卖方向的时候是basicToken数量，买方向是qouteToken 余额或限额
 */
export function useMaxOrderAmount(direction = null) {
    const store = useStore();
    const isUTA = computed(() => store.getters['user/isUTA']);
    const { isInnovate, remainQuota } = useInnovate();
    const baseBalanceRef = useBaseBalance();
    const qouteBalanceRef = useQuoteBalance();

    return computed(() => {
        const side = direction || store.state.exchange.buySell;

        const isLeverageTurnOn = store.state.leverage.isTurnOn;
        const { isUserCheckLeverage, isLeverage } = store.state.leverage;
        const {
            maxBuyAmt = '0', maxSellQty = '0', buyTotalAvailable = '0', sellTotalAvailable = '0',
        } = store.state.balance.accountInfo;
        const baseBalanceFree = baseBalanceRef.value?.free || '0';
        const qouteBalanceFree = qouteBalanceRef.value?.free || '0';

        // 未开启杠杆账户：即纯现货的逻辑，直接返回计价货币余额
        if (!isLeverageTurnOn) {
            return side === 'buy' ? qouteBalanceFree : baseBalanceFree;
        }
        // 以下为开启杠杆账户后的状态
        if (!isLeverage) {
            // 不支持杠杆的交易对，如果不是创新区币对，就直接用后端算出来的最大可买、最大可卖

            // 不支持杠杆的交易对，如果是创新区币对，并且是买方向，和持仓限额取小
            if (side === 'buy') {
                // 创新区货币，不支持杠杆币对，min(最大可买, 剩余限额), 可用额度返回'-'代表持仓限额无限大
                if (isInnovate.value && remainQuota.value !== '-') {
                    // 持仓限额无限的情况
                    // -- 可用余额 异常的情况 当作 0 处理
                    const remainAmount = remainQuota.value === '--' ? '0' : remainQuota.value;
                    return BN.min(isUTA.value ? qouteBalanceFree : maxBuyAmt, remainAmount);
                }

                return isUTA.value ? qouteBalanceFree : maxBuyAmt;
            }

            // side === sell
            return isUTA.value ? baseBalanceFree : maxSellQty;
        }

        // 支持杠杆的货币，但是用现货模式，就直接用可用余额作为最大可买卖
        if (!isUserCheckLeverage) {
            return side === 'buy' ? qouteBalanceFree : baseBalanceFree;
        }

        // 支持杠杆 并且 是杠杆模式
        return side === 'buy' ? buyTotalAvailable : sellTotalAvailable;
    });
}

/**
 * 当前币对是否在预告区
 *
 * 对应下面 对币对停牌状态的判断，增加了一个当前币对预告状态的 hook，代码中部分地方依旧是直接使用 的 noticeVisible 判断预告状态
 * 遇到直接使用 noticeVisible 的代码，可以酌情调整为此hook，达到代码逻辑收敛的目的
 */
export function useIsNoticeVisible() {
    const symbol = useSymbol();
    return computed(() => symbol.value?.noticeVisible);
}

/**
 *
 * 当前币对是否停牌
 *
 * 2022-08-22 币对增加停牌状态这个需求，在symbolList 接口上新增了 tradeStatus 完整表示币对所处状态：1: 未全量 2：预告 3：全量 4：停牌
 * 由于 tradeStatus 中的状态并不包含 白名单的判断， 在实际使用上 tradeStatus 作用不大，目前仅仅在停牌判断上，使用了此字段
 * 在 预告区判断上，还是优先使用 symbol.noticeVisible 字段
 * 币对状态梳理查看这个文档：
 * https://c1ey4wdv9g.larksuite.com/docx/doxuskuGMEpOGI82fEJqyCKmS5b
 *
 */
export function useIsSuspension() {
    const symbol = useSymbol();

    return computed(() => {
        const {
            tradeStatus, isBanBuy, isBanSell,
        } = symbol.value;
        // 后面的条件，表示用户没有全部开启 禁买、禁卖的白名单状态
        return tradeStatus === 4 && (isBanBuy || isBanSell);
    });
}

/**
 * 全仓协议&测试&开启提示
 *
 * @return { boolean } isInnovate   是否为创新区币种
 * @return { string }  remainQuota  剩余额度
 * @return { string }  totalQuota   总额度
 */
export function useCreateOrderTip() {
    const store = useStore();
    const { t } = useI18n({ useScope: 'global' });
    // 协议
    const isConfirmOpen = computed(() => store.state.leverage.isConfirmOpen);
    // 测试
    const isPassTest = computed(() => store.state.leverage.isPassTest);
    // 开启/关闭
    const isTurnOn = computed(() => store.state.leverage.isTurnOn);

    const isUTA = computed(() => store.getters['user/isUTA']);

    const marginMode = computed(() => store.state.user.marginModeValue);

    const checkLeverage = () => {
        // 如果用户UTA保证金模式是逐仓模式,不允许下单
        if (isUTA.value && marginMode.value === MARGIN_MODE_MEUM.ISOLATED_MARGIN) {
            errorToast(t('isolatedModeNotSupportTip'));
            return false;
        }

        if (!isConfirmOpen.value || !isPassTest.value) {
            store.dispatch('leverage/GET_LEVERAGE_INFO', true);
            return false;
        }

        if (!isTurnOn.value) {
            errorToast(t('leverage_off_trade_tip'));
            return false;
        }

        return true;
    };

    return {
        checkLeverage,
    };
}
